import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const PricingCards = ({ language }) => {
  const [paymentType, setPaymentType] = useState('onetime'); // 'onetime' or 'monthly'

  // Payment plans data with exact Stripe payment links and pet-themed titles
  const pricingPlans = [
    {
      name: language === 'zh' ? "小狗贊助" : "Puppy Sponsor",
      amount: 5,
      currency: "USD",
      paymentLink: "https://buy.stripe.com/28E8wQeXh3KB3Dp5iq0Fi0i",
      interval: null, // one-time
      features: language === 'zh' ? [
        '✓ 支持寵物紀念日開發',
        '✓ 幫助改善功能',
        '✓ 表達對項目的支持'
      ] : [
        '✓ Support Pet Days development',
        '✓ Help improve features',
        '✓ Show project appreciation'
      ],
      cta: language === 'zh' ? '贊助 $5' : 'Sponsor $5',
      popular: false
    },
    {
      name: language === 'zh' ? "貓咪贊助" : "Kitty Sponsor",
      amount: 8,
      currency: "USD",
      paymentLink: "https://buy.stripe.com/eVq00k3ez4OFgqb8uC0Fi0h",
      interval: null, // one-time
      features: language === 'zh' ? [
        '✓ 支持寵物紀念日開發',
        '✓ 幫助添加新功能',
        '✓ 優先功能建議',
        '✓ 表達更多支持'
      ] : [
        '✓ Support Pet Days development',
        '✓ Help add new features',
        '✓ Priority feature suggestions',
        '✓ Show greater support'
      ],
      cta: language === 'zh' ? '贊助 $8' : 'Sponsor $8',
      popular: true
    },
    {
      name: language === 'zh' ? "兔子贊助" : "Bunny Sponsor",
      amount: 10,
      currency: "USD",
      paymentLink: "https://buy.stripe.com/fZu9AU4iD94V4HtfX40Fi0g",
      interval: null, // one-time
      features: language === 'zh' ? [
        '✓ 支持寵物紀念日開發',
        '✓ 幫助添加新功能',
        '✓ 優先功能建議',
        '✓ 表達最大力度支持'
      ] : [
        '✓ Support Pet Days development',
        '✓ Help add new features',
        '✓ Priority feature suggestions',
        '✓ Express maximum support'
      ],
      cta: language === 'zh' ? '贊助 $10' : 'Sponsor $10',
      popular: false
    },
    {
      name: language === 'zh' ? "小狗月度贊助" : "Puppy Monthly Sponsor",
      amount: 5,
      currency: "USD",
      paymentLink: "https://buy.stripe.com/9B64gA8yT6WN2zl3ai0Fi0e",
      interval: "month",
      features: language === 'zh' ? [
        '✓ 每月持續支持',
        '✓ 幫助持續開發',
        '✓ 長期項目維護'
      ] : [
        '✓ Monthly ongoing support',
        '✓ Help continuous development',
        '✓ Long-term project maintenance'
      ],
      cta: language === 'zh' ? '每月 $5' : '$5/month',
      popular: false
    },
    {
      name: language === 'zh' ? "貓咪月度贊助" : "Kitty Monthly Sponsor",
      amount: 8,
      currency: "USD",
      paymentLink: "https://buy.stripe.com/00waEY16rdlb4HtaCK0Fi0d",
      interval: "month",
      features: language === 'zh' ? [
        '✓ 每月持續支持',
        '✓ 幫助持續開發',
        '✓ 長期項目維護',
        '✓ 優先功能建議',
        '✓ 表達更多支持'
      ] : [
        '✓ Monthly ongoing support',
        '✓ Help continuous development',
        '✓ Long-term project maintenance',
        '✓ Priority feature suggestions',
        '✓ Show greater support'
      ],
      cta: language === 'zh' ? '每月 $8' : '$8/month',
      popular: true
    },
    {
      name: language === 'zh' ? "兔子月度贊助" : "Bunny Monthly Sponsor",
      amount: 10,
      currency: "USD",
      paymentLink: "https://buy.stripe.com/bJe9AUbL580R1vh4em0Fi0f",
      interval: "month",
      features: language === 'zh' ? [
        '✓ 每月持續支持',
        '✓ 幫助持續開發',
        '✓ 長期項目維護',
        '✓ 優先功能建議',
        '✓ 最大力度支持開發'
      ] : [
        '✓ Monthly ongoing support',
        '✓ Help continuous development',
        '✓ Long-term project maintenance',
        '✓ Priority feature suggestions',
        '✓ Maximum development support'
      ],
      cta: language === 'zh' ? '每月 $10' : '$10/month',
      popular: false
    }
  ];

  const handlePayment = (paymentLink) => {
    // Open Stripe payment link in new tab
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  };

  // Filter plans based on selected payment type
  const filteredPlans = pricingPlans.filter(plan => 
    (paymentType === 'onetime' && !plan.interval) || 
    (paymentType === 'monthly' && plan.interval === 'month')
  );

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h4 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-2">
          {language === 'zh' ? '支持寵物紀念日' : 'Support Pet Days'}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {language === 'zh' 
            ? '您的贊助幫助我們開發更多功能和改善服務' 
            : 'Your sponsorship helps us develop more features and improve our service'
          }
        </p>
        
        {/* Payment Type Toggle */}
        <div className="inline-flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-4">
          <button
            onClick={() => setPaymentType('onetime')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              paymentType === 'onetime'
                ? 'bg-white dark:bg-gray-600 shadow text-gray-800 dark:text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center">
              <SafeIcon icon={FiIcons.FiZap} className="mr-1" />
              {language === 'zh' ? '一次性贊助' : 'One-time'}
            </div>
          </button>
          <button
            onClick={() => setPaymentType('monthly')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              paymentType === 'monthly'
                ? 'bg-white dark:bg-gray-600 shadow text-gray-800 dark:text-white'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center">
              <SafeIcon icon={FiIcons.FiCalendar} className="mr-1" />
              {language === 'zh' ? '月度贊助' : 'Monthly'}
            </div>
          </button>
        </div>
      </div>

      {/* Pricing Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {filteredPlans.map((plan, index) => (
          <motion.div
            key={`${paymentType}-${index}`}
            className={`rounded-xl overflow-hidden border ${
              plan.popular 
                ? 'border-love-300 dark:border-love-700 shadow-md ring-2 ring-love-200 dark:ring-love-800' 
                : 'border-gray-200 dark:border-gray-700'
            }`}
            whileHover={{ scale: 1.02, y: -5 }}
            transition={{ duration: 0.2 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
          >
            {plan.popular && (
              <div className="bg-love-500 text-white text-center py-1 text-xs font-medium">
                {language === 'zh' ? '最受歡迎' : 'Most Popular'}
              </div>
            )}
            <div className={`p-5 ${
              plan.popular 
                ? 'bg-love-50 dark:bg-love-900/20' 
                : 'bg-white dark:bg-gray-800'
            }`}>
              <div className="text-center mb-4">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">{plan.name}</h3>
                <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                  ${plan.amount}
                </span>
                <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">
                  {plan.interval 
                    ? `/${language === 'zh' ? '月' : 'month'}`
                    : language === 'zh' ? '一次性' : 'one-time'
                  }
                </span>
              </div>
              <ul className="space-y-2 mb-6 text-sm">
                {plan.features.map((feature, i) => (
                  <li key={i} className="text-gray-600 dark:text-gray-400">
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handlePayment(plan.paymentLink)}
                className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                  plan.popular
                    ? 'bg-love-500 hover:bg-love-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-300'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-6 text-xs text-gray-500 dark:text-gray-400">
        {language === 'zh' 
          ? '所有付款均通過 Stripe 安全處理。月度贊助可隨時取消。' 
          : 'All payments are securely processed through Stripe. Monthly subscriptions can be cancelled anytime.'
        }
      </div>
    </div>
  );
};

export default PricingCards;